import {
  AlertTriangle,
  Calendar,
  Cog,
  Droplets,
  Filter,
  FlaskConical,
  Hammer,
  Sparkles,
  Wrench,
} from "lucide-react";
import Reveal from "@/components/SectionReveal";
import { SERVICES } from "@/data/site";

const ICONS = {
  Sparkles,
  Calendar,
  Wrench,
  Filter,
  Cog,
  Droplets,
  AlertTriangle,
  FlaskConical,
  Hammer,
};

/**
 * Tetris-bento grid:
 * - Featured "Green Pool Cleanup" spans 2 cols x 2 rows
 * - Others fill in around it
 */
const SPANS = {
  "pool-cleaning": "md:col-span-1 md:row-span-1",
  "weekly-pool-service": "md:col-span-1 md:row-span-1",
  "pool-maintenance": "md:col-span-2 md:row-span-1",
  "pool-filter-cleaning": "md:col-span-1 md:row-span-1",
  "pool-pump-cleaning": "md:col-span-1 md:row-span-1",
  "green-pool-cleanup": "md:col-span-2 md:row-span-2",
  "pool-emergency": "md:col-span-2 md:row-span-1",
  "chemical-balancing": "md:col-span-1 md:row-span-1",
  "equipment-repair": "md:col-span-1 md:row-span-1",
};

export default function ServicesGrid() {
  return (
    <section id="services" className="relative py-24 sm:py-32 bg-gradient-to-b from-white to-sky-50/40">
      <div className="max-w-7xl mx-auto px-6 sm:px-10">
        <Reveal>
          <p className="text-xs tracking-[0.22em] uppercase font-bold text-sky-700">Our Services</p>
          <h2 className="mt-3 font-heading text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-slate-900 max-w-3xl">
            Everything your pool needs — done right, on time, every time.
          </h2>
          <p className="mt-4 max-w-2xl text-slate-600">
            From routine pool cleaning to full green-pool recovery and 24/7 emergency repair, our pros have
            you covered across Tampa Bay.
          </p>
        </Reveal>

        <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 auto-rows-auto md:auto-rows-[200px] gap-4 sm:gap-5">
          {SERVICES.map((s, i) => {
            const Icon = ICONS[s.icon] || Sparkles;
            const featured = s.featured;
            return (
              <Reveal
                key={s.slug}
                delay={i * 0.05}
                direction={i % 2 === 0 ? "up" : "left"}
                className={`group relative overflow-hidden rounded-3xl p-6 sm:p-7 border transition-all hover:-translate-y-1 ${SPANS[s.slug]} ${
                  featured
                    ? "bg-gradient-to-br from-sky-600 via-sky-500 to-cyan-400 text-white border-transparent shadow-xl shadow-sky-500/30"
                    : "bg-white text-slate-900 border-slate-100 shadow-[0_8px_30px_rgba(0,0,0,0.04)]"
                }`}
                testId={`service-card-${s.slug}`}
              >
                <div className="flex flex-col h-full">
                  <div
                    className={`w-12 h-12 rounded-2xl inline-flex items-center justify-center ${
                      featured ? "bg-white/15 text-white" : "bg-sky-50 text-sky-700"
                    }`}
                  >
                    <Icon className="w-6 h-6" />
                  </div>
                  <h3
                    className={`mt-4 font-heading font-bold tracking-tight ${
                      featured ? "text-2xl sm:text-3xl lg:text-4xl" : "text-lg"
                    }`}
                  >
                    {s.title}
                  </h3>
                  <p
                    className={`mt-2 text-sm leading-relaxed ${
                      featured ? "text-white/90 sm:text-base" : "text-slate-600"
                    }`}
                  >
                    {s.blurb}
                  </p>
                  {featured && (
                    <a
                      href="/contact"
                      data-testid="featured-service-cta"
                      className="mt-5 inline-flex w-fit items-center gap-2 px-5 py-2.5 rounded-full bg-amber-300 text-slate-900 font-bold text-sm hover:bg-amber-200 transition-colors"
                    >
                      Save my pool — Free quote
                    </a>
                  )}
                </div>

                {/* Decorative ripple for featured — clipped inside the card */}
                {featured && (
                  <svg
                    className="absolute -bottom-10 -right-10 w-56 h-56 opacity-15 pointer-events-none"
                    viewBox="0 0 200 200"
                  >
                    <circle cx="100" cy="100" r="40" fill="none" stroke="white" strokeWidth="2" />
                    <circle cx="100" cy="100" r="65" fill="none" stroke="white" strokeWidth="2" />
                    <circle cx="100" cy="100" r="90" fill="none" stroke="white" strokeWidth="2" />
                  </svg>
                )}
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}
